/**
 * Proposal Settings Tab (Phase 10c.1)
 *
 * Manages configurable proposal terminology:
 * - Document title (e.g., "Proposal", "Fee Letter", "Estimate")
 * - Section names (Understanding, Fee Structure, Assumptions, etc.)
 * - Item column labels (Description, Unit, Rate, Amount)
 */
import { useState, useEffect } from 'react';
import { useProposalConfig, useUpdateProposalConfig } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, Save, RotateCcw } from 'lucide-react';
import type { ProposalConfig } from '@/lib/schemas';

const DEFAULTS: ProposalConfig = {
  document_title: 'Proposal',
  section_names: {
    understanding: 'Understanding of Scope',
    fee_structure: 'Fee Structure',
    assumptions: 'Assumptions & Inclusions',
    inclusions: 'Inclusions',
    exclusions: 'Exclusions',
  },
  item_labels: {
    item_description: 'Description',
    unit_label: 'Unit',
    rate_label: 'Rate',
    amount_label: 'Amount',
  },
};

export function ProposalSettingsTab() {
  const { data, isLoading } = useProposalConfig();
  const updateConfig = useUpdateProposalConfig();

  const [formData, setFormData] = useState<ProposalConfig>(DEFAULTS);

  useEffect(() => {
    if (data) {
      setFormData({
        document_title: data.document_title ?? DEFAULTS.document_title,
        section_names: { ...DEFAULTS.section_names, ...data.section_names },
        item_labels: { ...DEFAULTS.item_labels, ...data.item_labels },
      });
    }
  }, [data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateConfig.mutateAsync(formData);
  };

  const handleReset = () => {
    setFormData(DEFAULTS);
  };

  const setDocumentTitle = (value: string) => {
    setFormData(prev => ({ ...prev, document_title: value }));
  };

  const setSectionName = (key: keyof ProposalConfig['section_names'], value: string) => {
    setFormData(prev => ({
      ...prev,
      section_names: { ...prev.section_names, [key]: value },
    }));
  };

  const setItemLabel = (key: keyof ProposalConfig['item_labels'], value: string) => {
    setFormData(prev => ({
      ...prev,
      item_labels: { ...prev.item_labels, [key]: value },
    }));
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <FileText className="w-5 h-5 text-slate-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Proposal Settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            Customize terminology used in proposal PDFs. Changes apply to all future PDF generations.
          </p>
        </div>
      </div>

      {/* Document Title */}
      <div>
        <label htmlFor="document-title" className="block text-sm font-medium text-gray-700 mb-2">
          Document Title
        </label>
        <Input
          id="document-title"
          value={formData.document_title}
          onChange={(e) => setDocumentTitle(e.target.value)}
          placeholder="Proposal"
          maxLength={60}
          className="max-w-sm"
        />
        <p className="text-xs text-gray-500 mt-1">
          Appears as the main heading on the PDF (e.g., "Proposal", "Fee Letter", "Estimate")
        </p>
      </div>

      {/* Section Names */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">Section Names</h3>
        <p className="text-xs text-gray-500 mb-4">
          Rename the sections that appear in proposal PDFs. Leave blank to hide a section.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(
            [
              { key: 'understanding', label: 'Understanding of Scope', hint: 'Project description section' },
              { key: 'fee_structure', label: 'Fee Structure', hint: 'Line items and pricing section' },
              { key: 'assumptions', label: 'Assumptions & Inclusions', hint: 'Scope assumptions list' },
              { key: 'inclusions', label: 'Inclusions', hint: 'Included scope items' },
              { key: 'exclusions', label: 'Exclusions', hint: 'Excluded scope items' },
            ] as const
          ).map(({ key, label, hint }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
              <Input
                value={formData.section_names[key]}
                onChange={(e) => setSectionName(key, e.target.value)}
                placeholder={DEFAULTS.section_names[key]}
                maxLength={80}
              />
              <p className="text-xs text-gray-400 mt-0.5">{hint}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Item Column Labels */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 mb-3">Fee Table Column Labels</h3>
        <p className="text-xs text-gray-500 mb-4">
          Rename the columns in the fee schedule table on proposal PDFs.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(
            [
              { key: 'item_description', label: 'Description' },
              { key: 'unit_label', label: 'Unit' },
              { key: 'rate_label', label: 'Rate' },
              { key: 'amount_label', label: 'Amount' },
            ] as const
          ).map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
              <Input
                value={formData.item_labels[key]}
                onChange={(e) => setItemLabel(key, e.target.value)}
                placeholder={DEFAULTS.item_labels[key]}
                maxLength={40}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          type="button"
          variant="ghost"
          onClick={handleReset}
          className="inline-flex items-center gap-2 text-gray-600"
        >
          <RotateCcw className="w-4 h-4" />
          Reset to Defaults
        </Button>
        <Button
          type="submit"
          disabled={updateConfig.isPending}
          className="inline-flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {updateConfig.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}
